import React from "react";
import styled, { css } from "styled-components";
import {
  Plate,
  createPlateComponents,
  createPlateOptions,
  createReactPlugin,
  createHistoryPlugin,
  createBasicElementPlugins,
  createBasicMarkPlugins,
  createBlockquotePlugin,
  createSoftBreakPlugin,
  createExitBreakPlugin,
  createListPlugin,
  createLinkPlugin,
  createDeserializeHTMLPlugin,
  serializeHTMLFromNodes,
  deserializeHTMLToDocumentFragment,
  KEYS_HEADING,
  createResetNodePlugin,
  ELEMENT_BLOCKQUOTE,
  ELEMENT_PARAGRAPH,
  ELEMENT_DEFAULT,
  ELEMENT_H1,
  ELEMENT_H2,
  isSelectionAtBlockStart,
  createEditorPlugins,
} from "@udecode/plate";
import { cloneDeep } from "lodash";
import RichTextEditorToolbar from "./RichTextEditorToolbar";

const ensureExternalLink = (link: string) => {
  if (link.startsWith("http://") || link.startsWith("https://")) {
    return link;
  }

  return `https://${link}`;
};

const DEFAULT_VALUE = [
  {
    type: ELEMENT_DEFAULT,
    children: [{ text: "" }],
  },
];

const RichTextEditor = (props: Props) => {
  const { error, id, placeholder, value, onChange } = props;

  const editableProps = {
    placeholder,
    spellCheck: false,
    autoFocus: true,
    style: {
      padding: "1rem",
      overflow: "auto",
      minHeight: "136px", // Four lines high.
      maxHeight: "320px",
    },
  };

  const resetBlockOptions = {
    rules: [
      {
        types: [ELEMENT_BLOCKQUOTE],
        defaultType: ELEMENT_PARAGRAPH,
        hotkey: "Enter",
        predicate: isSelectionAtBlockStart,
      },
      {
        types: [ELEMENT_BLOCKQUOTE],
        defaultType: ELEMENT_PARAGRAPH,
        hotkey: "Backspace",
        predicate: isSelectionAtBlockStart,
      },
    ],
  };

  const plugins = [
    createReactPlugin(),
    createHistoryPlugin(),
    createResetNodePlugin(resetBlockOptions),
    createSoftBreakPlugin({
      rules: [{ hotkey: "shift+enter" }],
    }),
    createExitBreakPlugin({
      rules: [
        {
          hotkey: "mod+enter",
        },
        {
          hotkey: "mod+shift+enter",
          before: true,
        },
        {
          hotkey: "enter",
          query: {
            start: true,
            end: true,
            allow: KEYS_HEADING,
          },
        },
      ],
    }),

    ...createBasicElementPlugins(),
    ...createBasicMarkPlugins(),
    createBlockquotePlugin(),
    createListPlugin(),
    {
      ...createLinkPlugin(),
      serialize: {
        element: (props) => {
          const newProps = cloneDeep(props);
          newProps.attributes.target = "_blank";
          newProps.element.url = ensureExternalLink(newProps.element.url);

          return createLinkPlugin().renderElement(editor)(newProps);
        },
      },
    },
  ];

  const HeadingOne = styled.h1`
    margin-top: 0;
    margin-bottom: 1rem;
  `;

  const HeadingTwo = styled.h2`
    margin-top: 0;
    margin-bottom: 0.5rem;
  `;

  const editor = createEditorPlugins();
  const options = createPlateOptions();
  const components = {
    ...createPlateComponents(),
    [ELEMENT_H1]: HeadingOne,
    [ELEMENT_H2]: HeadingTwo,
  };

  const initialValue = value
    ? deserializeHTMLToDocumentFragment(editor, { plugins, element: value })
    : DEFAULT_VALUE;

  return (
    <>
      <RichTextEditorToolbar />
      <RichTextbox error={error} id={id}>
        <Plate
          editor={editor}
          editableProps={editableProps}
          initialValue={initialValue}
          plugins={[...plugins, createDeserializeHTMLPlugin({ plugins })]}
          components={components}
          options={options}
          onChange={(nodes) => {
            const html = serializeHTMLFromNodes(editor, {
              plugins,
              nodes,
            });
            onChange(html);
          }}
        />
      </RichTextbox>
    </>
  );
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  name?: string;
  error?: boolean;
  placeholder?: string;
};

export const RichTextElementStyles = css`
  h1 {
    font-size: 24px;
    line-height: 1.5;
    margin-top: 0.75em;
    margin-bottom: 0.5rem;
  }

  h2 {
    font-size: 20px;
    line-height: 1.8;
    margin-top: 0.9em;
    margin-bottom: 0.5rem;
  }

  h1,
  h2 {
    font-weight: bold;
    line-height: 1.5;
  }

  p,
  ul,
  ol,
  blockquote {
    margin-top: 1.125em;
    line-height: 1.125;

    :first-child {
      margin-top: 0;
    }
  }
`;

const RichTextbox = styled.div<{
  error: boolean;
}>`
  margin-top: 0.25rem;
  font-family: ${({ theme }) => theme.font};
  color: ${({ theme }) => theme.colors.text};
  border-radius: 4px;
  border-width: 2px;
  border-style: solid;
  border-color: ${({ error, theme }) =>
    error ? theme.colors.error : theme.colors.border};
  box-sizing: border-box;
  width: 100%;
  font-size: 1rem;
  box-shadow: none;

  ${RichTextElementStyles}
`;

export default RichTextEditor;
